import { useSession, useUser } from '@clerk/react';
import Logo from '@components/atoms/LogoFull';
import SplashLoader from '@components/atoms/SplashLoader';
import { Box, Button, Link, Stack, Typography } from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useClerkMe } from 'src/contexts/ClerkMeContext';
import { useLoader } from 'src/contexts/LoaderContext';
import billingAPI from 'src/libs/api/policy/billing';
import { isClerkAuthEnabled, signOutClerk } from 'src/utils/clerkAuth';
import { getSupportUrl } from 'src/utils/links';

/**
 * Shared dead-end for login finalizers (password, magic link, SSO): the Clerk session resolved, but no Jooice
 * account is attached to it yet. Before showing the support copy, check whether the signed-in buyer left an
 * abandoned unpaid checkout behind: they get an explicit Resume payment CTA instead. The check runs for any
 * authenticated principal (pending/zero-org sessions included) and treats an unreachable API as "unknown",
 * never as "unpaid".
 */
const NoAccountPage = () => {
  const { t } = useTranslation('auth', { keyPrefix: 'no-account' });
  const { hideLoader } = useLoader();
  const { data: clerkMe, isError: clerkMeError } = useClerkMe();
  const navigate = useNavigate();

  // Principal readiness comes only from Clerk, not from the /v1/me response: an error there is an
  // honest "cannot check" state, never a reason to show the loader forever.
  const { isLoaded: principalLoaded, isSignedIn: principalSignedIn, user: clerkUser } = useUser();
  const { session } = useSession();
  const principalReady = isClerkAuthEnabled() && principalLoaded && principalSignedIn && Boolean(clerkUser?.id);

  // Principal-based check: the auth token is enough; a missing active account is NOT a skip condition.
  // The cache key is pinned to the Clerk user id so a later login cannot see another account's URL.
  const pending = useQuery({
    queryKey: ['pendingPurchase', principalReady ? clerkUser?.id : null, principalReady ? session?.id : null],
    queryFn: () => billingAPI.getPendingPurchase(),
    enabled: principalReady,
    select: (res) => res,
    staleTime: 30_000,
  });

  const marketCountry = { us: 'usa', it: 'ita', mea: 'mea' }[clerkMe?.activeAccount?.market?.toLowerCase() ?? ''];
  const supportUrl = getSupportUrl(marketCountry ?? null);

  const pendingClient = useQueryClient();

  // Sensitive resume URL leaves the cache at the auth boundary and stays pinned to the login identity.
  const handleSignOut = async () => {
    await signOutClerk();
    pendingClient.removeQueries({ predicate: (query) => query.queryKey[0] === 'pendingPurchase' });
    navigate('/auth', { replace: true });
  };

  useEffect(() => {
    hideLoader();
  }, [hideLoader]);

  const identityUnavailable = principalReady && clerkMeError;

  // Loader only while the Clerk principal itself is still resolving; an unknown principal, a disabled
  // check, or an /v1/me error resolves to an honest "cannot check" page, never to a permanent splash.
  const stillLoading =
    !principalLoaded ||
    (principalReady && !clerkMeError && pending.fetchStatus === 'fetching' && !pending.data && !pending.isError);

  if (stillLoading) return <SplashLoader />;

  let titleKey = 'title';
  let descriptionKey = 'description';
  let resumeUrl: string | null = null;

  if (identityUnavailable || pending.isError || (principalReady && pending.isPending)) {
    // /v1/me or the pending endpoint failing must never be presented as "no purchase behind you".
    titleKey = 'check-unavailable-title';
    descriptionKey = 'check-unavailable';
  } else {
    switch (pending.data?.status) {
      case 'pending':
        titleKey = 'resume-title';
        descriptionKey = 'resume-description';
        resumeUrl = pending.data.url ?? null;
        break;
      case 'processing':
        titleKey = 'processing-title';
        descriptionKey = 'processing-description';
        break;
      case 'expired':
        titleKey = 'expired-title';
        descriptionKey = 'expired-description';
        break;
      default:
        break;
    }
  }

  return (
    <Stack minHeight="100%" alignItems="center" justifyContent="center" padding={3}>
      <Stack maxWidth={480} width="100%" alignItems="center" textAlign="center" spacing={3}>
        <Box marginBottom={1}>
          <Logo disabledLink />
        </Box>
        <Typography variant="h4" color="text.primary" fontWeight="bold">
          {t(titleKey)}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {t(descriptionKey)}
        </Typography>
        {resumeUrl && (
          <Button onClick={() => resumeUrl && window.location.assign(resumeUrl)} fullWidth size="large">
            {t('resume-payment')}
          </Button>
        )}
        <Link href={supportUrl} target="_blank" rel="noopener noreferrer" underline="hover" fontWeight="bold">
          {t('contact-support')}
        </Link>
        <Button onClick={handleSignOut} variant="outlined">
          {t('sign-out')}
        </Button>
      </Stack>
    </Stack>
  );
};

export default NoAccountPage;
